import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link2, Loader2 } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { organisationsAPI } from '../../services/api';
import toast from '../../services/toast';
import { useConfirm } from '../../contexts/ConfirmContext';
import Input from '../../components/ui/Input';
import FormField from '../../components/forms/FormField';

export default function LinkGitHubModal({ isOpen, onClose, orgId, member, onSuccess }: any) {
  const { confirm } = useConfirm();
  const [unlinkingId, setUnlinkingId] = useState<any>(null);
  const [linkedContributors, setLinkedContributors] = useState<any[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { githubUsername: '' } });

  useEffect(() => {
    if (!isOpen || !member) return;
    reset({ githubUsername: '' });
    setLinkedContributors(
      Array.isArray(member.linkedContributors) ? [...member.linkedContributors] : []
    );
  }, [isOpen, member, reset]);

  if (!member) return null;

  const memberLabel = member.username || member.email || 'member';

  const onSubmit = handleSubmit(async ({ githubUsername }) => {
    const username = String(githubUsername || '').trim();
    if (!username) {
      toast.error('Enter a GitHub username');
      return;
    }
    if (!orgId || !member.id) return;
    try {
      const data = await organisationsAPI.linkMemberToGitHub(orgId, member.id, {
        githubUsername: username,
      });
      toast.success(`Linked @${username} to ${memberLabel}`);
      setLinkedContributors(Array.isArray(data.linkedContributors) ? data.linkedContributors : []);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to link');
    }
  });

  const handleUnlink = async (contributor) => {
    const login = (contributor.githubUsername || '').trim();
    const ok = await confirm({
      title: 'Unlink GitHub account',
      message: `Unlink @${login} from ${memberLabel}?`,
      confirmLabel: 'Unlink',
    });
    if (!ok || !orgId || !member.id || contributor.githubId == null) return;
    setUnlinkingId(contributor.githubId);
    try {
      const data = await organisationsAPI.unlinkMemberGitHub(
        orgId,
        member.id,
        contributor.githubId
      );
      setLinkedContributors(Array.isArray(data.linkedContributors) ? data.linkedContributors : []);
      toast.success(`Unlinked @${login}`);
      onSuccess?.();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to unlink');
    } finally {
      setUnlinkingId(null);
    }
  };

  const usernameRegister = register('githubUsername', {
    onChange: (e) => {
      e.target.value = e.target.value.replace(/^@+/, '');
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Link GitHub account"
      icon={<Link2 className="w-5 h-5 text-vivid-red" aria-hidden />}
      contentClassName="max-w-md"
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-dark-border bg-dark-elevated/50 px-3 py-2">
          <p className="text-sm font-medium text-slate-100">{memberLabel}</p>
          {member.email ? <p className="text-sm text-slate-400">{member.email}</p> : null}
        </div>

        <form onSubmit={onSubmit} className="space-y-2" noValidate>
          <FormField id="link-github-username" label="GitHub username">
            <Input
              id="link-github-username"
              type="text"
              placeholder="e.g. torvalds"
              autoComplete="off"
              disabled={isSubmitting}
              {...usernameRegister}
            />
          </FormField>
          <div className="flex flex-wrap gap-2 pt-2">
            <button type="submit" className="btn-primary-mdl" disabled={isSubmitting}>
              {isSubmitting ? 'Linking…' : 'Link account'}
            </button>
            <button
              type="button"
              className="btn-secondary-mdl"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>

        {linkedContributors.length > 0 ? (
          <div className="border-t border-dark-border pt-4 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Currently linked
            </p>
            <ul className="space-y-2">
              {linkedContributors.map((contributor) => {
                const login = contributor.githubUsername || '';
                const busy = unlinkingId === contributor.githubId;
                return (
                  <li
                    key={contributor.githubId ?? login}
                    className="flex items-center justify-between gap-2 rounded-lg border border-dark-border px-3 py-2"
                  >
                    <span className="text-sm text-vivid-green-cyan truncate">@{login}</span>
                    <button
                      type="button"
                      className="btn-secondary-mdl text-xs shrink-0 disabled:opacity-50"
                      disabled={busy || isSubmitting}
                      onClick={() => handleUnlink(contributor)}
                    >
                      {busy ? (
                        <span className="inline-flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          Unlinking…
                        </span>
                      ) : (
                        'Unlink'
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
